import React, { useCallback, useEffect, useContext, useState, useMemo } from 'react'
import { MainButton } from '../../../Button/MainButton'
import { Dialog, DialogTitle, DialogContent, makeStyles, MenuItem, TextField } from '@material-ui/core'
import { FONT_WEIGHTS, formatNumsToDollars } from '../../../../utils'
import BoardingWizardContext from '../BoardingWizardContext'
import useBoardingVisit from '../../../../hooks/useBoardingVisit'
import PetAvatar from '../../../Common/PetAvatar'
import { dangers, gray } from '../../../../utils/colors'
import StyledAlert from '../../../Common/StyledAlert'
import StyledSwitch from '../../../Common/StyledSwitch'
import { STANDARD_KENNEL_OPTIONS } from '../constants'

const useStyles = makeStyles(() => ({
	content: { display: 'flex', flexDirection: 'column', gap: '1rem' },
	header: {
		marginBottom: '.8rem'
	},
	titleText: {
		fontWeight: FONT_WEIGHTS.ExtraBold,
		fontSize: '1rem',
		textTransform: 'uppercase'
	},
	petsContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
		'& > div:last-child': {
			// borderBottomWidth: 0,
			// paddingBottom: 0
		}
	},
	petContainer: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		borderBottom: `1px solid ${gray[500]}`,
		paddingBottom: '2rem',
		gap: '1.75rem'
	},
	actionsContainer: {
		justifyContent: 'flex-start',
		marginTop: '1rem',
		display: 'flex',
		flexDirection: 'column',
		gap: '1rem',
		marginTop: '1rem'
	},
	deleteButton: {
		alignSelf: 'flex-start',
		color: dangers[500],
		fontFamily: 'Nunito',
		fontSize: '0.875rem',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		letterSpacing: '0.078125rem',
		textTransform: 'uppercase',
		padding: '.5rem 0 !important'
	},
	mainActions: {
		display: 'flex',
		flexDirection: 'row',
		alignSelf: 'flex-start',
		gap: '1rem',
		'& .MuiButton-outlinedSizeLarge': {
			height: 'unset'
		}
	},
	divider: {
		height: '0.0625rem',
		backgroundColor: gray[500],
		margin: '1.5rem 0'
	},
	addonDatesPickerContainer: {
		'& .rmdp-container': {
			width: '100%'
		}
	},
	subOptionsSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: '.6rem',
		'& .rmdp-container': {
			width: '100%'
		}
	},
	sectionHeader: {
		fontFamily: 'Nunito',
		fontWeight: FONT_WEIGHTS.ExtraBold,
		fontSize: '0.75rem',
		textTransform: 'uppercase',
		letterSpacing: '0.047rem'
	},
	switchContainer: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		backgroundColor: gray[200],
		height: '3.5rem',
		borderRadius: '0.375rem',
		padding: '0 .875rem',
		'& div:first-child': {
			flex: 1
		}
	}
}))

const EditOvernightUpgradesDialog = ({ open, onSave, onCancel, title = 'Overnight Upgrades' }) => {
	const classes = useStyles()

	const {
		wizardContextProvider: { wizard_state }
	} = useContext(BoardingWizardContext)
	const { updating, boarding, facility } = useBoardingVisit()

	const [boardingOptions, setBoardingOptions] = useState([])
	const [sharingKennels, setSharingKennels] = useState(false)

	const luxuryPrice = useMemo(() => {
		const { kennel_options = [] } = facility
		return kennel_options.find(({ type }) => type === 'Luxury')?.price || 1000
	}, [facility])

	const hasOneLuxuryUpgrade = useMemo(() => {
		return boardingOptions.some(({ kennel_type }) => kennel_type === 'Luxury')
	}, [boardingOptions])

	const warningText = useMemo(() => {
		if (!boarding || !facility) {
			return ''
		}

		const { overnights_price } = boarding

		return `Each shared kennel accommodates a maximum of two dogs. One of the dogs in this appointment will occupy a separate kennel, at a rate of ${formatNumsToDollars(
			overnights_price
		)}/night. Please also note the Overnight Upgrade fee of ${formatNumsToDollars(
			luxuryPrice
		)}/night applies to each kennel.`
	}, [boarding, facility, luxuryPrice])

	const singleKennelRequiredForOneDog = useMemo(() => {
		const { pets } = wizard_state
		if (sharingKennels) {
			return pets.length % 2 !== 0
		}
		return false
	}, [wizard_state, sharingKennels])

	useEffect(() => {
		const { boarding_options = [], sharing_kennels } = wizard_state
		setBoardingOptions([...boarding_options])
		setSharingKennels(sharing_kennels)
	}, [wizard_state, setBoardingOptions, setSharingKennels])

	const petMap = useMemo(() => {
		const { pets, boarding_options } = wizard_state
		const map = {}
		boarding_options.forEach((option) => {
			const { pet_id } = option
			const pet = pets.find((pet) => pet._id === pet_id)
			map[pet_id] = pet
		})

		return map
	}, [wizard_state])

	const handleOnSave = useCallback(() => {
		if (onSave) {
			onSave({ boarding_options: boardingOptions, sharing_kennels: sharingKennels })
		}
	}, [onSave, boardingOptions, sharingKennels])

	const handleOnCancel = useCallback(() => {
		if (onCancel) {
			onCancel()
		}
	}, [onCancel])

	const handleSelection = useCallback(
		(e, pet_id) => {
			const { value } = e.target
			const updatedOptions = boardingOptions.map((option) => {
				if (pet_id) {
					if (option.pet_id === pet_id) {
						return {
							...option,
							kennel_type: value
						}
					}
					return option
				} else {
					return {
						...option,
						kennel_type: value
					}
				}
			})
			setBoardingOptions(updatedOptions)
		},
		[boardingOptions, setBoardingOptions]
	)

	const handleSharingKennelsChange = useCallback(
		(e) => {
			const { checked } = e.target
			setSharingKennels(e.target.checked)
			if (checked === true && hasOneLuxuryUpgrade) {
				handleSelection({ target: { value: 'Luxury' } }, null)
			}
		},
		[setSharingKennels, hasOneLuxuryUpgrade, handleSelection]
	)

	const options = useMemo(() => {
		if (!facility) {
			return []
		}

		const { kennel_options } = facility || []

		const availableOptions = kennel_options.filter((option) => STANDARD_KENNEL_OPTIONS.includes(option.type))

		const o = availableOptions.map((option) => {
			const { price, active, public_name, type } = option
			if (!active) {
				return null
			}

			let title = public_name

			if (price > 0) {
				title = `${title} +${formatNumsToDollars(price)} / Night`
			} else {
				title = `${title} - Free`
			}

			return (
				<MenuItem key={type} value={type}>
					{title}
				</MenuItem>
			)
		})

		return o
	}, [facility])

	return (
		<>
			<Dialog
				PaperProps={{
					style: {
						width: 450,
						maxWidth: 450,
						minWidth: 450
					}
				}}
				maxWidth="md"
				open={open}
				onClose={() => null}>
				<DialogTitle>
					<div className={classes.header}>
						<div className={classes.titleText}>{`Edit ${title}`}</div>
					</div>
				</DialogTitle>
				<DialogContent className={classes.content}>
					<div>
						Please select the dates you would like to schedule an Overnight Upgrade for each pet listed
						on this appointment.
					</div>
					<div className={classes.petsContainer}>
						<div className={classes.switchContainer}>
							<div>Sharing with Sibling</div>
							<StyledSwitch checked={sharingKennels} onChecked={handleSharingKennelsChange} />
						</div>
						{sharingKennels ? (
							<>
								{boardingOptions.map((option, i) => {
									const { pet_id } = option
									const petDetails = petMap[pet_id]
									return (
										<div key={petDetails._id}>
											<PetAvatar petName={petDetails.name} imageUrl={petDetails.image_url} />
										</div>
									)
								})}
								<div className={classes.subOptionsSection}>
									<div className={classes.sectionHeader}>Night Kennel</div>
									<TextField
										className="noMinWidth"
										select
										label="Night Program"
										variant="filled"
										value={hasOneLuxuryUpgrade ? 'Luxury' : 'Standard'}
										onChange={(e) => handleSelection(e, null)}>
										{options}
									</TextField>
								</div>
							</>
						) : null}
						{!sharingKennels
							? boardingOptions.map((option, i) => {
									const { pet_id, kennel_type } = option
									const petDetails = petMap[pet_id]
									return (
										<>
											<div key={petDetails._id} className={classes.petContainer}>
												<PetAvatar
													petName={petDetails.name}
													imageUrl={petDetails.image_url}
												/>
												<div className={classes.subOptionsSection}>
													<div className={classes.sectionHeader}>Night Kennel</div>
													<TextField
														className="noMinWidth"
														select
														label="Night Program"
														variant="filled"
														value={kennel_type}
														onChange={(e) => handleSelection(e, pet_id)}>
														{options}
													</TextField>
												</div>
											</div>
										</>
									)
							  })
							: null}
						{singleKennelRequiredForOneDog ? <StyledAlert type="warning" text={warningText} /> : null}
					</div>
				</DialogContent>
				<div className={classes.actionsContainer}>
					<div className={classes.mainActions}>
						<MainButton
							style={{ paddingTop: '6px' }}
							variant="contained"
							label="Save"
							onClick={handleOnSave}
							disabled={updating}
						/>
						<MainButton label="Cancel" onClick={handleOnCancel} disabled={updating} />
					</div>
				</div>
			</Dialog>
		</>
	)
}

export default EditOvernightUpgradesDialog
